import { useRef, useState, type ReactNode } from 'react';
import { Upload } from 'lucide-react';
import {
  SUPPORTED_UPLOAD_ACCEPT,
  SUPPORTED_UPLOAD_TYPES_LABEL,
  isSupportedUploadFile,
} from '../constants/upload';

const BRAND_BG = 'bg-emerald-800';
const BRAND_BORDER = 'border-emerald-800';

interface FileDropZoneProps {
  /** Called with the files that have a supported type (unsupported ones are filtered out). */
  onFiles: (files: File[]) => void;
  /** Called with the files that were dropped or selected but have an unsupported type. */
  onRejected?: (files: File[]) => void;
  title: string;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  /** Rendered below the description; clicks inside do not open the file picker. */
  children?: ReactNode;
}

export default function FileDropZone({
  onFiles,
  onRejected,
  title,
  multiple = false,
  disabled = false,
  className = '',
  children,
}: FileDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (fileList: FileList | null) => {
    if (disabled || !fileList || fileList.length === 0) return;
    const all = Array.from(fileList);
    const accepted = all.filter(isSupportedUploadFile);
    const rejected = all.filter((f) => !isSupportedUploadFile(f));

    if (accepted.length > 0) onFiles(multiple ? accepted : accepted.slice(0, 1));
    if (rejected.length > 0) onRejected?.(rejected);
  };

  return (
    <div
      className={`
        flex flex-col items-center justify-center border-2 border-dashed rounded-3xl transition-all duration-300
        ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
        ${isDragging ? `${BRAND_BORDER} bg-emerald-50 scale-[1.01]` : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'}
        ${className}
      `}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        handleFiles(e.dataTransfer.files);
      }}
      onClick={() => !disabled && fileInputRef.current?.click()}
    >
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        multiple={multiple}
        accept={SUPPORTED_UPLOAD_ACCEPT}
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <div className={`w-20 h-20 ${BRAND_BG} rounded-full flex items-center justify-center text-white mb-6 shadow-xl`}>
        <Upload className="w-10 h-10" />
      </div>
      <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">{title}</h2>
      <p className="text-gray-500 mb-8 max-w-md text-center">
        Drag and drop your file here, or click to browse. <br />
        <span className="text-xs text-gray-400 mt-2 block">{SUPPORTED_UPLOAD_TYPES_LABEL}</span>
      </p>

      {children && <div onClick={(e) => e.stopPropagation()}>{children}</div>}
    </div>
  );
}
